import React, { useState, useEffect } from 'react';
import { PencilIcon, EyeIcon, RotateCcwIcon, CheckIcon, XIcon, ArrowLeftIcon, RefreshCcwIcon, Link2Icon } from 'lucide-react';
import { PageLoader } from '../components/common';
import { Link } from 'react-router-dom';
import { tenantApi } from '../lib/tenant-api';
import type { CTA } from '../lib/tenant-api';

interface Prompt {
  id: string;
  tenant_id: string;
  prompt_type: string;
  custom_content: string | null;
  is_enabled: boolean;
  is_customized: boolean;
  effective_content: string;
}

/**
 * Widget `parseCtaEmbeds` expects:
 * `[label](url)(contentType, ctaOrFlowId[, "optional title"][, _blank|_self])`
 * See lawma-ai-widget markdownParser.ts
 */
function escapeTitleForCtaEmbed(raw: string): string {
  return raw.replace(/\r?\n/g, ' ').replace(/"/g, "'").trim();
}

function mapLinkTypeToTarget(linkType?: string): '_blank' | '_self' {
  const v = (linkType || '').trim().toLowerCase();
  if (v === '_self' || v === 'current') return '_self';
  return '_blank';
}

function getCtaPromptEmbedParts(cta: CTA): {
  label: string;
  url: string;
  contentType: string;
  embedId: string;
  target: '_blank' | '_self';
} | null {
  const label = (cta.text || cta.name || '').trim();
  if (!label) return null;

  if (cta.cta_type === 'appointment_booking') {
    const flowId = (cta.interactive_flow_id || '').trim();
    if (!flowId) return null;
    return {
      label,
      url: '#',
      contentType: 'cta_interactive_flow',
      embedId: flowId,
      target: '_self',
    };
  }

  const link = (cta.link || '').trim();
  if (!link) return null;

  const contentType = cta.cta_type === 'form_full' ? 'cta_full' : 'cta_generic';
  return {
    label,
    url: link,
    contentType,
    embedId: cta.id,
    target: mapLinkTypeToTarget(cta.link_type),
  };
}

function formatCtaPromptEntry(cta: CTA): string {
  const p = getCtaPromptEmbedParts(cta);
  if (!p) return '';
  const titleRaw = (cta.title || cta.content || '').replace(/\r?\n/g, ' ').trim();
  let line = `[${p.label}](${p.url})(${p.contentType}, ${p.embedId}`;
  if (titleRaw) {
    line += `, "${escapeTitleForCtaEmbed(titleRaw)}"`;
  }
  line += `, ${p.target})`;
  return line;
}

function ctaCanBeEmbeddedInPrompt(cta: CTA): boolean {
  return getCtaPromptEmbedParts(cta) !== null;
}

const RELEVANT_LINK_TITLE = 'Relevant link for users:';

/** Remove one line containing `toRemove` from content, and one surrounding newline so no blank line is left. */
function removeLineFromContent(content: string, toRemove: string): string {
  if (!content || !toRemove || !content.includes(toRemove)) return content;
  const patterns: [string, string][] = [
    [`\n${toRemove}\n`, '\n'],
    [`\n${toRemove}`, ''],
    [`${toRemove}\n`, ''],
  ];
  for (const [pattern, replacement] of patterns) {
    if (content.includes(pattern)) return content.replace(pattern, replacement);
  }
  return content.replace(toRemove, '');
}

/** Append CTA lines under the "Relevant link for users:" section. If the heading doesn't exist, add it above the first line. */
function appendUnderRelevantLinkTitle(content: string, linesToAdd: string[]): string {
  if (linesToAdd.length === 0) return content;
  const block = linesToAdd.join('\n');
  if (content.includes(RELEVANT_LINK_TITLE)) {
    const start = content.indexOf(RELEVANT_LINK_TITLE);
    const sectionEnd = content.indexOf('\n\n', start);
    const end = sectionEnd === -1 ? content.length : sectionEnd;
    return content.slice(0, end) + '\n' + block + content.slice(end);
  }
  const withHeading = RELEVANT_LINK_TITLE + '\n' + block;
  return content ? content + '\n\n' + withHeading : withHeading;
}

const AdvancedSettings: React.FC = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState<boolean>(false);
  const [editContent, setEditContent] = useState<string>('');
  const [previewContent, setPreviewContent] = useState<string>('');
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewGoal, setPreviewGoal] = useState<string>('assist you');
  const [saving, setSaving] = useState<boolean>(false);
  const [clearingCache, setClearingCache] = useState<boolean>(false);
  const [cacheMessage, setCacheMessage] = useState<string>('');
  const [cacheMessageType, setCacheMessageType] = useState<'success' | 'error' | ''>('');
  const [showIncludeCTAsModal, setShowIncludeCTAsModal] = useState<boolean>(false);
  const [ctas, setCtas] = useState<CTA[]>([]);
  const [loadingCTAs, setLoadingCTAs] = useState<boolean>(false);
  const [selectedCtaIds, setSelectedCtaIds] = useState<string[]>([]);
  const [savingIncludeCTAs, setSavingIncludeCTAs] = useState<boolean>(false);

  // Load prompts on component mount
  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const response = await tenantApi.getPrompts();
      setPrompts(response);
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPrompt = (): Prompt | undefined => {
    // Since we only have general type now, just get the first (and only) prompt
    return prompts.find(p => p.prompt_type === 'general') || prompts[0];
  };

  const handleEdit = (prompt: Prompt) => {
    setEditingPrompt(true);
    setEditContent(prompt.custom_content || '');
  };

  const handleSave = async () => {
    if (!editingPrompt) return;

    try {
      setSaving(true);
      await tenantApi.updatePrompt({
        custom_content: editContent,
        is_enabled: true
      });

      // Reload prompts to get updated data
      await loadPrompts();
      setEditingPrompt(false);
      setEditContent('');
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingPrompt(false);
    setEditContent('');
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset this prompt to the default template?')) {
      return;
    }

    try {
      setSaving(true);
      await tenantApi.deletePromptCustomization();
      await loadPrompts();
    } catch (error) {
      console.error('Error resetting prompt:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const response = await tenantApi.previewPrompt({
        prompt_type: 'general',
        custom_content: editingPrompt ? editContent : undefined,
        goal: previewGoal
      });
      setPreviewContent(response.preview_content);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  };

  const handleOpenIncludeCTAs = async () => {
    setShowIncludeCTAsModal(true);
    try {
      setLoadingCTAs(true);
      const data = await tenantApi.getCTAs();
      setCtas(data);
      const current = getCurrentPrompt();
      const existingContent = (current?.custom_content || '').trim();
      const alreadyInContent = data
        .filter(cta => ctaCanBeEmbeddedInPrompt(cta) && existingContent.includes(formatCtaPromptEntry(cta)))
        .map(cta => cta.id);
      setSelectedCtaIds(alreadyInContent);
    } catch (error) {
      console.error('Error loading CTAs:', error);
    } finally {
      setLoadingCTAs(false);
    }
  };

  const handleToggleCtaSelection = (ctaId: string) => {
    setSelectedCtaIds(prev =>
      prev.includes(ctaId) ? prev.filter(id => id !== ctaId) : [...prev, ctaId]
    );
  };

  const handleSaveIncludeCTAs = async () => {
    const current = getCurrentPrompt();
    if (!current) return;
    try {
      setSavingIncludeCTAs(true);
      let content = (current.custom_content || '').trim();
      const selectedSet = new Set(selectedCtaIds);
      // Remove lines for CTAs that are in content but now unchecked
      for (const cta of ctas) {
        if (!ctaCanBeEmbeddedInPrompt(cta)) continue;
        const line = formatCtaPromptEntry(cta);
        if (line && content.includes(line) && !selectedSet.has(cta.id)) {
          content = removeLineFromContent(content, line);
        }
      }
      // Add lines for selected CTAs that are not already in content, under "Relevant link for users:"
      const linesToAdd: string[] = [];
      for (const id of selectedCtaIds) {
        const cta = ctas.find(c => c.id === id);
        if (!cta || !ctaCanBeEmbeddedInPrompt(cta)) continue;
        const line = formatCtaPromptEntry(cta);
        if (line && !content.includes(line)) linesToAdd.push(line);
      }
      if (linesToAdd.length > 0) {
        content = appendUnderRelevantLinkTitle(content, linesToAdd);
      }
      await tenantApi.updatePrompt({ custom_content: content, is_enabled: true });
      await loadPrompts();
      setShowIncludeCTAsModal(false);
      setSelectedCtaIds([]);
    } catch (error) {
      console.error('Error adding/removing CTAs in prompt:', error);
    } finally {
      setSavingIncludeCTAs(false);
    }
  };

  const handleClearCache = async () => {
    try {
      setClearingCache(true);
      setCacheMessage('');
      setCacheMessageType('');

      const response = await tenantApi.clearPromptCache();
      setCacheMessage(response.message);
      setCacheMessageType('success');

      // Clear the message after 5 seconds
      setTimeout(() => {
        setCacheMessage('');
        setCacheMessageType('');
      }, 5000);
    } catch (error) {
      console.error('Error clearing cache:', error);
      setCacheMessage('Failed to clear cache. Please try again.');
      setCacheMessageType('error');

      // Clear the message after 5 seconds
      setTimeout(() => {
        setCacheMessage('');
        setCacheMessageType('');
      }, 5000);
    } finally {
      setClearingCache(false);
    }
  };

  const currentPrompt = getCurrentPrompt();

  if (loading) {
    return <PageLoader />;
  }

  if (!currentPrompt) {
    return (
      <div className="text-center text-gray-500">
        <p className="text-lg font-medium">No prompt configuration found</p>
        <p className="text-sm mt-1">
          Unable to load your AI assistant prompt customization
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb Navigation */}
      <div className="mb-4">
        <Link
          to="/settings"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Settings
        </Link>
        <nav className="flex mt-2" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/settings" className="text-gray-500 hover:text-gray-700">
                Settings
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                </svg>
                <span className="ml-1 text-gray-500 md:ml-2">Advanced Prompt Management</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Advanced Prompt Management</h1>
            <p className="text-gray-600 mt-2 max-w-[800px]">
              Customize your AI assistant's behavior by adding specific instructions that will be applied to all conversations.
              Your customizations work with the unified AI system that handles all types of interactions.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleClearCache}
              disabled={clearingCache}
              className="inline-flex items-center px-4 py-2 border border-orange-300 shadow-sm text-sm font-medium  text-orange-700 bg-white hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCcwIcon className={`h-4 w-4 mr-2 ${clearingCache ? 'animate-spin' : ''}`} />
              {clearingCache ? 'Refreshing...' : 'Refresh Cache'}
            </button>
          </div>
        </div>

        {/* Cache Message Display */}
        {cacheMessage && (
          <div className={`mt-4 p-3  border ${cacheMessageType === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
            }`}>
            <div className="flex items-center">
              <div className="text-sm">{cacheMessage}</div>
            </div>
          </div>
        )}
      </div>

      {/* Single Prompt Editor */}
      <div className="bg-white shadow ">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                AI Assistant Custom Instructions
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {currentPrompt.is_customized ? 'You have custom instructions active' : 'Using default AI behavior'}
              </p>
            </div>
            <div className="flex space-x-2">
              {!editingPrompt && (
                <>
                  <button
                    onClick={handleOpenIncludeCTAs}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Link2Icon className="h-4 w-4 mr-1" />
                    Include CTAs
                  </button>
                  <button
                    onClick={() => handleEdit(currentPrompt)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={handlePreview}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Preview
                  </button>
                  {currentPrompt.is_customized && (
                    <button
                      onClick={handleReset}
                      disabled={saving}
                      className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm font-medium  text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                    >
                      <RotateCcwIcon className="h-4 w-4 mr-1" />
                      Reset
                    </button>
                  )}
                </>
              )}
              {editingPrompt && (
                <>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium  text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <CheckIcon className="h-4 w-4 mr-1" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <XIcon className="h-4 w-4 mr-1" />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {editingPrompt ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Instructions for Your AI Assistant
                </label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={15}
                  className="w-full border border-gray-300  px-3 py-2 text-sm font-mono"
                  placeholder="Add your custom instructions here. These will be combined with the base AI system to enhance your assistant's behavior.

Examples:
- Always mention our 30-day return policy when discussing products
- Ask if customers want to speak with a specialist for technical questions
- Be extra helpful and patient with first-time customers
- Focus on our company's commitment to sustainability
- Always suggest related products when appropriate"
                />
                <p className="text-xs text-gray-500 mt-1">
                  These instructions will be added to the base AI system. Available variables: {'{goal}'}
                </p>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preview Goal (for testing)
                  </label>
                  <input
                    type="text"
                    value={previewGoal}
                    onChange={(e) => setPreviewGoal(e.target.value)}
                    className="w-full border border-gray-300  px-3 py-2 text-sm"
                    placeholder="Enter a goal for preview..."
                  />
                </div>
                <button
                  onClick={handlePreview}
                  className="mt-6 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50"
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  Preview Changes
                </button>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Current Custom Instructions
              </label>
              <div className="bg-gray-50 border border-gray-200  p-4 min-h-[200px]">
                {currentPrompt.custom_content ? (
                  <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                    {currentPrompt.custom_content}
                  </pre>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No custom instructions added yet. Click "Edit" to add your own customizations that will enhance the base AI system behavior for your specific business needs.
                  </div>
                )}
              </div>
              {currentPrompt.custom_content && (
                <p className="text-xs text-gray-500 mt-2">
                  These instructions are automatically combined with the base AI system for all interactions.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg  bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">AI Assistant Prompt Preview</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="bg-gray-50 border border-gray-200  p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                  {previewContent}
                </pre>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700  hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Include CTAs Modal */}
      {showIncludeCTAsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg  bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Include CTAs in prompt</h3>
                <button
                  onClick={() => { setShowIncludeCTAsModal(false); setSelectedCtaIds([]); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Select one or more CTAs to append to the bottom of your custom instructions.
              </p>
              {loadingCTAs ? (
                <PageLoader fullScreen={false} />
              ) : (
                <>
                  <div className="max-h-80 overflow-y-auto border border-gray-200  mb-4">
                    {ctas.filter(cta => ctaCanBeEmbeddedInPrompt(cta)).length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No embeddable CTAs. Add a link to a CTA, or an appointment booking CTA with a flow configured.
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {ctas
                          .filter(cta => ctaCanBeEmbeddedInPrompt(cta))
                          .map(cta => (
                            <li key={cta.id}>
                              <label htmlFor={`include-cta-${cta.id}`} className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                                <input
                                  id={`include-cta-${cta.id}`}
                                  type="checkbox"
                                  checked={selectedCtaIds.includes(cta.id)}
                                  onChange={() => handleToggleCtaSelection(cta.id)}
                                  className="mt-1 h-4 w-4  border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  aria-label={`Include CTA: ${cta.name}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium text-gray-900">{cta.name}</span>
                                  <p className="text-xs text-gray-500 truncate">
                                    {cta.cta_type === 'appointment_booking'
                                      ? `# (appointment flow: ${(cta.interactive_flow_id || '').slice(0, 8)}…)`
                                      : cta.link}
                                  </p>
                                </div>
                              </label>
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { setShowIncludeCTAsModal(false); setSelectedCtaIds([]); }}
                      className="px-4 py-2 border border-gray-300 text-gray-700  hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveIncludeCTAs}
                      disabled={savingIncludeCTAs}
                      className="px-4 py-2 bg-indigo-600 text-white  hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingIncludeCTAs ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSettings;
